import React from 'react'
import { Navigate } from 'react-router-dom'
import { DotLottieReact } from '@lottiefiles/dotlottie-react'
import { useAuth } from '../../contexts/AuthContext'
import AuthComponent from './AuthComponent'
import PendingAccess from '../../pages/PendingAccess/PendingAccess'
import AccountBlocked from '../../pages/AccountBlocked/AccountBlocked'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowUnapproved?: boolean
  skipApprovalCheck?: boolean
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowUnapproved = false, skipApprovalCheck = false }) => {
  const { session, loading, isApproved, isBlocked } = useAuth()
  const loadingAnimationSrc = import.meta.env.VITE_LOADING_LOTTIE_URL as string | undefined

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        {loadingAnimationSrc ? (
          <div className="h-56 w-56" aria-label="Loading animation">
            <DotLottieReact src={loadingAnimationSrc} loop autoplay />
          </div>
        ) : (
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        )}
      </div>
    )
  }

  if (!session) {
    return <AuthComponent />
  }

  if (skipApprovalCheck) {
    return <>{children}</>
  }

  if (isBlocked) {
    return <AccountBlocked />
  }

  if (!isApproved && !allowUnapproved) {
    return <PendingAccess />
  }

  if (isApproved && allowUnapproved) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

export default ProtectedRoute
